import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  ChoiceSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NotPermitted,
  ReasonDialog
} from '@/design';
import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type {
  IFreezeChange,
  IRankingEdition,
  IRankingFreezeChanges,
  IRankingOverview
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { InternalFieldDisplay } from '../../components/field-controls';
import { formatDay } from '../../components/days';
import { recordSurveyPath } from '../../components/participant-link';
import { ORIGIN_WORDS, wordFor } from '../../figures';
import { RANKING_REFUSAL_WORDS, SNAPSHOT_READ, rankingRefusalReason } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { sentence } from '../person-hub/said';
import { useRanking } from './ranking';

/**
 * A ranking's record-level writes (SR-231, RP §10), in the record's header beside "Read again". On
 * an edition on no cycle — migrated, or placed by hand — with no snapshot and no scored result,
 * **Cycle…** links the survey cycle it reads (an edition made on its cycle has it fixed, SR-451), and
 * **Publish…** makes the draft final — confirmed, with "Close gaps first" (`?compact=true`). On a
 * published ranking, **Back to draft…** (RP §10.3, the client's demo of 16 Oct) makes it a draft
 * again — confirmed, a reason optional, and said plainly what a draft does not protect. On a draft,
 * **Update snapshot…** (RP §10.4, SR-451; `refreeze` on the wire) moves the moment the metrics
 * archive is read at forward, to now or a moment the slider picks — confirmed, said what moves and
 * that it only moves forward; `figures.freeze`'s, drawn disabled with
 * the reason for anyone else, apart from `rankings.manage`'s presses. Each sends the record's
 * `ETag` and hands the record the one-ranking read it answers.
 *
 * **The cycles offered are those pinning the survey** — the record's read of `…/export/cycles`,
 * the Export tab's too, `internal_fields.read`'s — since survey-api refuses any other (400 at
 * `cycleCode`). Where that read failed the code is typed, and survey-api's 400 says it.
 *
 * **Refusals are said in the dialog** (`role="alert"`), which stays open: `ranking-not-contiguous`
 * names the territories that are not numbered 1..N — a gap closes with "Close gaps first", a
 * duplicate place has to be moved — and a stale `ETag` asks for "Read again". A ranking whose status
 * moved meanwhile closes the dialog and reads the record again.
 */
export function RecordActions() {
  const { overview, permitted, snapshotOpen, setSnapshotOpen } = useRanking();
  const canFreeze = useCan()('figures.freeze');
  const reasonId = React.useId();
  const freezeReasonId = React.useId();
  const [open, setOpen] = React.useState<Nullable<'cycle' | 'publish' | 'draft'>>(null);
  if (overview?.ranking.status === 'published') {
    return (
      <span className="grid justify-items-end gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!permitted}
          aria-describedby={permitted ? undefined : reasonId}
          onClick={() => setOpen('draft')}
        >
          Back to draft…
        </Button>
        {!permitted && <NotPermitted id={reasonId} what="taking a ranking back to a draft" />}
        <BackToDraftDialog open={open === 'draft'} onClose={() => setOpen(null)} />
      </span>
    );
  }
  if (overview?.ranking.status !== 'draft') return null;
  const press = (
    which: 'refreeze' | 'cycle' | 'publish',
    label: string,
    allowed = permitted,
    reason = reasonId
  ) => (
    <Button
      type="button"
      size="sm"
      variant={which === 'publish' ? 'default' : 'outline'}
      disabled={!allowed}
      aria-describedby={allowed ? undefined : reason}
      onClick={() => (which === 'refreeze' ? setSnapshotOpen(true) : setOpen(which))}
    >
      {label}
    </Button>
  );
  return (
    <span className="grid justify-items-end gap-1">
      <span className="flex gap-2">
        {/* A snapshot is updated once there is one: the first export takes it (SR-451). */}
        {overview?.ranking.frozenAt && press('refreeze', 'Update snapshot…', canFreeze, freezeReasonId)}
        {/* An edition made on its cycle has it fixed (SR-451): only one on none — migrated, or
            placed by hand — with no snapshot and no scores has it set by hand. */}
        {!overview?.ranking.imported &&
          !overview?.ranking.frozenAt &&
          !overview?.ranking.cycleCode &&
          press('cycle', 'Cycle…')}
        {press('publish', 'Publish…')}
      </span>
      {!permitted && <NotPermitted id={reasonId} what="linking a cycle or publishing a ranking" />}
      {!canFreeze && <NotPermitted id={freezeReasonId} what="updating a ranking’s snapshot" />}
      <RefreezeDialog open={snapshotOpen} onClose={() => setSnapshotOpen(false)} />
      <CycleDialog open={open === 'cycle'} onClose={() => setOpen(null)} />
      <PublishDialog open={open === 'publish'} onClose={() => setOpen(null)} />
    </span>
  );
}

/** What a refused record write says — or `null` once it has read the record again instead. */
function useRefused(onGone: () => void) {
  const record = useRanking();
  return (failure: unknown): Nullable<string> => {
    if (failure instanceof ApiError && failure.isProblem(ProblemType.revisionConflict)) {
      return 'Someone changed this ranking since this page read it, so nothing was saved. Press Refresh and try again.';
    }
    const reason = rankingRefusalReason(failure);
    if (reason === 'ranking-published' || reason === 'ranking-draft') {
      onGone();
      void record.readAgain();
      return null;
    }
    if (reason === 'ranking-not-contiguous') {
      const named = failure instanceof ApiError ? failure.problem?.territories : undefined;
      const territories = Array.isArray(named)
        ? named.map((territory) =>
            typeof territory === 'string' ? territory : 'the national list'
          )
        : [];
      return territories.length > 0
        ? `Not published: ${territories.join(', ')} ${territories.length === 1 ? 'is' : 'are'} not numbered 1 to N. “Close gaps first” closes a gap; a place held twice has to be moved first.`
        : wordFor(RANKING_REFUSAL_WORDS, reason);
    }
    if (reason !== null) return wordFor(RANKING_REFUSAL_WORDS, reason);
    return sentence(refusalMessage(failure, 'The change was refused'));
  };
}

/** Runs one record write under the held `ETag` and adopts its answer: `null` once landed. */
function useRecordWrite() {
  const record = useRanking();
  return async (
    send: (etag: string) => Promise<IEtagged<IRankingOverview>>,
    rows: boolean,
    refused: (failure: unknown) => Nullable<string>
  ): Promise<Nullable<string> | 'landed'> => {
    if (record.etag === null)
      return 'This ranking came without its version — press Refresh.';
    try {
      record.adoptRead(await send(record.etag), rows);
      return 'landed';
    } catch (failure) {
      return refused(failure);
    }
  };
}

function PublishDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { key, overview } = useRanking();
  const write = useRecordWrite();
  const refused = useRefused(onClose);
  const [compact, setCompact] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);

  const publish = async () => {
    setBusy(true);
    setSaid(null);
    const answer = await write(
      (etag) => rankingProcessApi.publish(key, etag, compact),
      true,
      refused
    );
    setBusy(false);
    if (answer === 'landed') onClose();
    else setSaid(answer);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy || next) return;
        setSaid(null);
        onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Publish {overview?.ranking.name}</AlertDialogTitle>
          <AlertDialogDescription>
            Publishing makes the placements the final list: the participants’ pages show them, and
            this ranking takes no import and no renumbering until someone takes it back to a draft.
            Every territory has to be numbered 1 to N.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={compact}
            disabled={busy}
            onCheckedChange={(next) => setCompact(next === true)}
          />
          Close gaps first — renumber each territory 1 to N in order of place
        </label>
        {said && (
          <p role="alert" className="text-sm text-destructive">
            {said}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(event) => {
              event.preventDefault();
              void publish();
            }}
          >
            {busy ? 'Publishing…' : 'Publish'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** What a refreeze would change: loading, failed or read. */
type FreezeChangesRead =
  | { state: 'reading' }
  | { state: 'failed'; message: string }
  | { state: 'read'; to: Nullable<string>; changes: IRankingFreezeChanges };

/** What a refreeze to `to` (null for now) would change, read afresh as the dialog opens and each
 *  time the moment is moved (SR-451): every figure at the linked cycle's as-of date that reads
 *  otherwise at `to` than at the freeze, whoever moved it — so the press is made knowing whose
 *  numbers move, another list's staff fix included. A read answers the moment it was asked for: one
 *  for another moment — the slider moved since, or the last visit's — is drawn as still reading. */
function useFreezeChanges(open: boolean, to: Nullable<string>): FreezeChangesRead {
  const { key } = useRanking();
  const [read, setRead] = React.useState<FreezeChangesRead>({ state: 'reading' });
  React.useEffect(() => {
    setRead({ state: 'reading' });
    if (!open) return;
    const controller = new AbortController();
    // The slider moves in steps: a read waits for it to settle.
    const timer = window.setTimeout(() => {
      rankingProcessApi.freezeChanges(key, to, controller.signal).then(
        (changes) => {
          if (!controller.signal.aborted) setRead({ state: 'read', to, changes });
        },
        (failure: unknown) => {
          if (controller.signal.aborted) return;
          setRead({
            state: 'failed',
            message: sentence(
              refusalMessage(failure, 'What changed since the snapshot could not be read')
            )
          });
        }
      );
    }, 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, key, to]);
  return read.state === 'read' && read.to !== to ? { state: 'reading' } : read;
}

function amountOrNone(value: Nullable<number>, none: string): React.ReactNode {
  return value === null ? (
    <span className="text-muted-foreground">{none}</span>
  ) : (
    <InternalFieldDisplay payload={{ t: 'money', v: value }} />
  );
}

/** The snapshot's move, said whole: the moment held, an arrow, the moment chosen. */
function FreezeMove({ from, to }: { from: string; to: Nullable<string> }) {
  return (
    <div
      className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-md border border-border bg-muted/40 p-3"
      data-freeze-move=""
    >
      <div className="grid gap-0.5">
        <span className="text-xs text-muted-foreground">Snapshot now</span>
        <span className="font-medium tabular-nums" data-freeze-from={from}>
          {formatDateTime(from)}
        </span>
      </div>
      <span aria-hidden="true" className="text-xl text-muted-foreground">
        →
      </span>
      <div className="grid gap-0.5">
        <span className="text-xs text-muted-foreground">New snapshot</span>
        <span className="font-medium tabular-nums" data-freeze-to={to ?? 'now'}>
          {to === null ? 'Now — the moment you press Update' : formatDateTime(to)}
        </span>
      </div>
    </div>
  );
}

/** The moment chosen, on a slider over the moments a row was recorded since the snapshot: each
 *  stop takes in the rows recorded up to it, the last is now. A native range, so the keyboard and
 *  a screen reader drive it as they drive any slider. */
function FreezeMoments({
  moments,
  index,
  onIndex
}: {
  moments: string[];
  index: number;
  onIndex: (index: number) => void;
}) {
  const id = React.useId();
  if (moments.length === 0) return null;
  const last = moments.length;
  const words = (at: number) =>
    at === last ? 'Now' : `${formatDateTime(moments[at])} — change ${at + 1} of ${moments.length}`;
  return (
    <div className="grid gap-1" data-freeze-moments={moments.length}>
      <label htmlFor={id} className="text-sm font-medium">
        Take the new snapshot at
      </label>
      <input
        id={id}
        type="range"
        min={0}
        max={last}
        step={1}
        value={index}
        onChange={(event) => onIndex(Number(event.target.value))}
        aria-valuetext={words(index)}
        className="w-full accent-[var(--primary)]"
      />
      <div className="grid grid-cols-[1fr_auto_1fr] text-xs text-muted-foreground">
        <span>{formatDateTime(moments[0])}</span>
        <span className="font-medium text-foreground">
          {index === last ? '' : words(index)}
        </span>
        <span className="text-right">Now</span>
      </div>
    </div>
  );
}

/** The other editions of the ranking's cycle and their snapshots, read as the dialog opens: lists
 *  of one cycle with different snapshots read different archives, and this is where to align them. */
function SiblingSnapshots({ open }: { open: boolean }) {
  const { key, overview } = useRanking();
  const cycle = overview?.ranking.cycleCode ?? null;
  const [siblings, setSiblings] = React.useState<Nullable<IRankingEdition[]>>(null);
  React.useEffect(() => {
    if (!open || cycle === null) return;
    const controller = new AbortController();
    rankingProcessApi.editions(key.surveyCode, controller.signal).then(
      (editions) =>
        setSiblings(
          editions.filter(
            (edition) =>
              edition.cycleCode === cycle &&
              !(edition.list === key.list && String(edition.year) === String(key.year))
          )
        ),
      () => setSiblings(null)
    );
    return () => controller.abort();
  }, [open, cycle, key]);
  if (cycle === null || siblings === null || siblings.length === 0) return null;
  return (
    <div className="text-xs text-muted-foreground" data-sibling-snapshots={siblings.length}>
      <span className="font-medium text-foreground">Other lists of cycle {cycle}:</span>{' '}
      {siblings.map((edition, index) => (
        <span key={`${edition.list}-${edition.year}`}>
          {index > 0 && ' · '}
          {edition.name} —{' '}
          {edition.frozenAt ? `snapshot ${formatDateTime(edition.frozenAt)}` : 'no snapshot yet'}
        </span>
      ))}
    </div>
  );
}

/** One participant's moved figures, grouped from the change list (in its order, by name). */
interface IParticipantChanges {
  participantId: string;
  displayName: string;
  figures: IFreezeChange[];
}

function byParticipant(items: IFreezeChange[]): IParticipantChanges[] {
  const groups: IParticipantChanges[] = [];
  for (const change of items) {
    const last = groups[groups.length - 1];
    if (last?.participantId === change.participantId) last.figures.push(change);
    else
      groups.push({
        participantId: change.participantId,
        displayName: change.displayName,
        figures: [change]
      });
  }
  return groups;
}

/** The figures an update would move, one row per participant, under the dialog's sentence. */
function FreezeChangesList({
  read,
  surveyCode,
  to
}: {
  read: FreezeChangesRead;
  surveyCode: string;
  to: Nullable<string>;
}) {
  if (read.state === 'reading') {
    return <p className="text-sm text-muted-foreground">Reading what changed since the snapshot…</p>;
  }
  if (read.state === 'failed') {
    return (
      <p role="alert" className="text-sm text-destructive">
        {read.message}
      </p>
    );
  }
  const { asOf, changes } = read.changes;
  if (asOf === null) {
    return (
      <p className="text-sm text-muted-foreground" data-freeze-changes="none">
        The ranking’s cycle names no as-of date, so no figure is read to compare.
      </p>
    );
  }
  const until =
    to === null ? 'since the snapshot' : `between the snapshot and ${formatDateTime(to)}`;
  if (changes.total === 0) {
    return (
      <p className="text-sm" data-freeze-changes="0">
        No figure as of {formatDay(asOf)} changed {until}: a new snapshot changes no number in the
        file.
      </p>
    );
  }
  return (
    <div className="grid gap-2" data-freeze-changes={changes.total}>
      <p className="text-sm">
        <strong>
          {changes.total} {changes.total === 1 ? 'figure' : 'figures'} of {changes.participants}{' '}
          {changes.participants === 1 ? 'participant' : 'participants'}
        </strong>{' '}
        as of {formatDay(asOf)} changed {until}. The new snapshot takes every one of them into the
        next export — a change made for another list of this survey included.
        {changes.items.length < changes.total && ` The first ${changes.items.length} are listed.`}
      </p>
      <div className="max-h-[40dvh] overflow-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-2 py-1 font-medium">Participant</th>
              <th className="px-2 py-1 font-medium">Figure</th>
              <th className="px-2 py-1 text-right font-medium">In the snapshot</th>
              <th className="px-2 py-1 text-right font-medium">
                {to === null ? 'Now' : 'In the new snapshot'}
              </th>
              <th className="px-2 py-1 font-medium">Changed by</th>
            </tr>
          </thead>
          <tbody>
            {byParticipant(changes.items).map((group) => {
              // One submit, one import or one edit moves several figures at once: said once.
              const first = group.figures[0];
              const shared = group.figures.every(
                (change) =>
                  change.source === first.source &&
                  change.actorEmail === first.actorEmail &&
                  change.recordedAt === first.recordedAt
              );
              return group.figures.map((change, index) => (
                <tr
                  key={`${change.participantId}-${change.metric}-${change.scope}`}
                  className={index === 0 ? 'border-t border-border align-top' : 'align-top'}
                  data-freeze-change={`${change.participantId}:${change.metric}.${change.scope}`}
                >
                  {index === 0 && (
                    <td className="px-2 py-1" rowSpan={group.figures.length}>
                      <Link
                        className="underline"
                        to={recordSurveyPath(group.participantId, surveyCode)}
                        target="_blank"
                      >
                        {group.displayName}
                        <span className="sr-only"> (opens in a new tab)</span>
                      </Link>
                    </td>
                  )}
                  <td className="whitespace-nowrap px-2 py-0.5">
                    {change.label} <span className="text-muted-foreground">{change.scope}</span>
                  </td>
                  <td className="px-2 py-0.5 text-right tabular-nums">
                    {amountOrNone(change.frozen, '—')}
                  </td>
                  <td className="px-2 py-0.5 text-right tabular-nums">
                    {amountOrNone(change.now, 'Cleared')}
                  </td>
                  {(!shared || index === 0) && (
                    <td
                      className="px-2 py-0.5 text-xs text-muted-foreground"
                      rowSpan={shared ? group.figures.length : undefined}
                    >
                      <span className="text-foreground">{wordFor(ORIGIN_WORDS, change.source)}</span>
                      <span className="block">{formatDateTime(change.recordedAt)}</span>
                      <span className="block break-all">{change.actorEmail ?? 'no account'}</span>
                    </td>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RefreezeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { key, overview } = useRanking();
  const canFreeze = useCan()('figures.freeze');
  const reasonId = React.useId();
  const write = useRecordWrite();
  const refused = useRefused(onClose);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const frozenAt = overview?.ranking.frozenAt ?? null;
  const imported = overview?.ranking.imported === true;
  // The stops are the first read's, kept for the visit of the dialog; the slider starts at now.
  const [moments, setMoments] = React.useState<string[]>([]);
  const [index, setIndex] = React.useState(0);
  const to = index < moments.length ? moments[index] : null;
  const changes = useFreezeChanges(open && frozenAt !== null, to);
  React.useEffect(() => {
    if (!open) {
      setMoments([]);
      setIndex(0);
    }
  }, [open]);
  // The stops come from this visit's read to now, never the last visit's: a moment at or before
  // a snapshot an update has since moved to would be refused `freeze-backwards`.
  React.useEffect(() => {
    if (!open || changes.state !== 'read' || changes.to !== null || moments.length > 0) return;
    const stops = changes.changes.changes.moments;
    if (stops.length === 0) return;
    setMoments(stops);
    setIndex(stops.length);
  }, [open, changes, moments.length]);

  const refreeze = async () => {
    setBusy(true);
    setSaid(null);
    const answer = await write((etag) => rankingProcessApi.refreeze(key, etag, to), false, refused);
    setBusy(false);
    if (answer === 'landed') onClose();
    else setSaid(answer);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (busy || next) return;
        setSaid(null);
        onClose();
      }}
    >
      <AlertDialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Update the snapshot of {overview?.ranking.name}</AlertDialogTitle>
          <AlertDialogDescription>
            The scoring input reads the metrics archive as it stood at this draft’s snapshot:{' '}
            {SNAPSHOT_READ}. A new snapshot moves that moment forward, so the next export reads everything recorded
            until then, whoever recorded it. A snapshot only moves forward; the History tab keeps the
            step.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {/* Every way in opens it on a draft holding a snapshot. */}
        {frozenAt !== null && (
          <div className="grid gap-4">
            <FreezeMove from={frozenAt} to={to} />
            {imported && (
              <p
                className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm"
                data-snapshot-imported=""
              >
                A scored result is already imported into this draft. A new snapshot changes the next
                export only: the placements stay as scored until a scored result of the new export is
                imported.
              </p>
            )}
            <FreezeMoments moments={moments} index={index} onIndex={setIndex} />
            <SiblingSnapshots open={open} />
            <FreezeChangesList read={changes} surveyCode={key.surveyCode} to={to} />
          </div>
        )}
        {said && (
          <p role="alert" className="text-sm text-destructive">
            {said}
          </p>
        )}
        {!canFreeze && <NotPermitted id={reasonId} what="updating a ranking’s snapshot" />}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || !canFreeze}
            aria-describedby={canFreeze ? undefined : reasonId}
            onClick={(event) => {
              event.preventDefault();
              void refreeze();
            }}
          >
            {busy
              ? 'Updating…'
              : to === null
                ? 'Update snapshot to now'
                : `Update snapshot to ${formatDateTime(to)}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BackToDraftDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { key, overview } = useRanking();
  const write = useRecordWrite();
  const refused = useRefused(onClose);
  return (
    <ReasonDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={`Take ${overview?.ranking.name ?? 'this ranking'} back to a draft`}
      required={false}
      confirmLabel="Back to draft"
      description={
        <span className="grid gap-2">
          <span>
            The ranking stops being final: the participants’ pages and the ranking fields stop
            showing it, and its placements can be inserted, moved and removed here again. Publishing
            it again checks that every territory is numbered 1 to N. The History tab keeps this
            step and its reason.
          </span>
          <span>
            <strong>What a draft does not protect.</strong> Importing a scored result replaces every
            placement of the draft, edits made since included. A participant’s name, firm, city and
            state are read from their record, not kept in the ranking: their next survey answers,
            and the BrokerCheck and IAPD refresh for the fields it fills, can change them — correct
            the record itself.
          </span>
        </span>
      }
      onConfirm={async (reason) => {
        const answer = await write(
          (etag) => rankingProcessApi.returnToDraft(key, etag, reason),
          true,
          refused
        );
        return answer === 'landed' ? null : answer;
      }}
    />
  );
}

const NO_CYCLE = '';

function CycleDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { key, overview, exportCycles, exportCyclesError } = useRanking();
  const write = useRecordWrite();
  const refused = useRefused(onClose);
  const current = overview?.ranking.cycleCode ?? NO_CYCLE;
  const [code, setCode] = React.useState(current);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const cycleLabel = React.useId();
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setCode(current);
      setSaid(null);
    }
  }

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setSaid(null);
    const trimmed = code.trim();
    const answer = await write(
      (etag) => rankingProcessApi.linkCycle(key, etag, trimmed === NO_CYCLE ? null : trimmed),
      false,
      refused
    );
    setBusy(false);
    if (answer === 'landed') onClose();
    else setSaid(answer);
  };

  const offered = exportCycles?.cycles ?? null;
  // The cycles are still being read: a box that turned into a list under the reader's typing
  // would drop what they typed, so the list's place waits, disabled. A failed read types.
  const reading = exportCycles === null && exportCyclesError === null;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent>
        <form
          aria-label="Survey cycle"
          onSubmit={(event) => void save(event)}
        >
          <DialogHeader>
            <DialogTitle>Survey cycle</DialogTitle>
            <DialogDescription>
              The cycle whose answers fed this edition. It stays linked once the ranking is
              published.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {offered || reading ? (
              <div className="grid gap-1 text-sm">
                <span id={cycleLabel} className="text-muted-foreground">
                  Cycle
                </span>
                <ChoiceSelect
                  labelledBy={cycleLabel}
                  value={code}
                  disabled={busy || reading}
                  onChange={setCode}
                  choices={[
                    { value: NO_CYCLE, label: reading ? 'Reading the cycles…' : 'No cycle' },
                    ...(offered ?? []).map((cycle) => ({
                      value: cycle.code,
                      label: `${cycle.code} — ${cycle.name}`
                    })),
                    ...(code !== NO_CYCLE && !(offered ?? []).some((cycle) => cycle.code === code)
                      ? [{ value: code, label: code }]
                      : [])
                  ]}
                />
              </div>
            ) : (
              <label className="grid gap-1 text-sm">
                <span className="text-muted-foreground">Cycle</span>
                <Input
                  value={code}
                  disabled={busy}
                  placeholder="a cycle’s code — empty for none"
                  onChange={(event) => setCode(event.target.value)}
                />
              </label>
            )}
            {said && (
              <p role="alert" className="text-sm text-destructive">
                {said}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || reading || code.trim() === current}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
